/*
 *
 * PgIncidents constants
 *
 */
import React from 'react';
import moment from 'moment';

import { Space, Text, Popup } from '@cashfree-intl/coherent';
import { DEFAULT_DATE_TIME_PAYLOAD_FORMAT } from 'utils/constants';

import CustomIcons from 'components/CustomIcons';

import { StyledFlexRow, StyledFlexText, StyledSpan } from './styled';

export const BASE_ACTION_CONSTANT = 'app/PgIncidents';

export const DEFAULT_ACTION = `${BASE_ACTION_CONSTANT}/DEFAULT_ACTION`;

export const SEARCH_INCIDENTS = `${BASE_ACTION_CONSTANT}/SEARCH_INCIDENTS`;

export const SAVE_INCIDENTS = `${BASE_ACTION_CONSTANT}/SAVE_INCIDENTS`;

export const GET_INCIDENTS_SUMMARY = `${BASE_ACTION_CONSTANT}/GET_INCIDENTS_SUMMARY`;

export const SAVE_INCIDENTS_SUMMARY = `${BASE_ACTION_CONSTANT}/SAVE_INCIDENTS/SUMMARY`;

export const UPDATE_TABLE_DATA = `${BASE_ACTION_CONSTANT}/UPDATE_TABLE_DATA`;

export const RESET_FILTERS = `${BASE_ACTION_CONSTANT}/RESET_FILTERS`;

export const UPDATE_FILTERS = `${BASE_ACTION_CONSTANT}/UPDATE_FILTERS`;

export const RESET_PAGE = `${BASE_ACTION_CONSTANT}/RESET_PAGE`;

export const RESET_DATA = `${BASE_ACTION_CONSTANT}/RESET_DATA`;

export const ENABLE_LOADER = `${BASE_ACTION_CONSTANT}/ENABLE_LOADER`;

export const DISABLE_LOADER = `${BASE_ACTION_CONSTANT}/DISABLE_LOADER`;

export const UPDATE_CARD_STATUS = `${BASE_ACTION_CONSTANT}/UPDATE_CARD_STATUS`;

export const CUSTOM_DATE_LIMIT = `${BASE_ACTION_CONSTANT}/CUSTOM_DATE_LIMIT`;

export const ACTIVE = 'ACTIVE';

export const UPCOMING = 'UPCOMING';

export const RESOLVED = 'RESOLVED';

export const popupText = key => {
  switch (key) {
    case 'startDate':
      return (
        <div>
          <Text color="bodyLight">
            It denotes the start date and time of the particular incident.
          </Text>
          <StyledFlexRow>
            <StyledFlexText>Note:</StyledFlexText>
            <Text color="bodyLight">
              1. “Est” signifies the estimated downtime of the incident.
            </Text>
          </StyledFlexRow>

          <StyledFlexRow>
            <StyledFlexText color="bodyLight" pl="8">
              2.
            </StyledFlexText>
            <Text color="bodyLight">
              The Updated tag indicates new estimated time of the incident.
            </Text>
          </StyledFlexRow>
        </div>
      );
    case 'endDate': {
      return (
        <div>
          <Text color="bodyLight">
            It denotes the end date and time of the particular incident.
          </Text>
          <StyledFlexRow>
            <StyledFlexText>Note:</StyledFlexText>
            <Text color="bodyLight">
              1. “Est” signifies the estimated downtime of the incident.
            </Text>
          </StyledFlexRow>

          <StyledFlexRow>
            <StyledFlexText color="bodyLight" pl="8">
              2.
            </StyledFlexText>
            <Text color="bodyLight">
              The Updated tag indicates new estimated time of the incident.
            </Text>
          </StyledFlexRow>
        </div>
      );
    }
    case 'severity': {
      return (
        <Text color="bodyLight">
          Severity indicates the level of seriousness associated with an
          incident, which can be categorized as high, medium, or low, based on
          its impact and implications.
        </Text>
      );
    }
    default:
      return null;
  }
};

export const HEADINGS = [
  {
    key: 'paymentMode',
    displayName: 'Payment Mode',
  },
  {
    key: 'entityDowntime',
    displayName: 'Entity with Downtime',
  },
  {
    key: 'startDate',
    displayName: (
      <Space
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="center"
      >
        <Text>Start Date & Time</Text>
        <Popup
          content={popupText('startDate')}
          trigger={
            <StyledSpan>
              <CustomIcons componentName="INFO" />
            </StyledSpan>
          }
        />
      </Space>
    ),
    toolTip: true,
  },
  {
    key: 'endDate',
    displayName: (
      <Space
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="center"
      >
        <Text>End Date & Time</Text>
        <Popup
          content={popupText('endDate')}
          trigger={
            <StyledSpan>
              <CustomIcons componentName="INFO" />
            </StyledSpan>
          }
        />
      </Space>
    ),
    toolTip: true,
  },
  {
    key: 'duration',
    displayName: 'Duration',
  },
  {
    key: 'incidentType',
    displayName: 'Incident Type',
  },
  {
    key: 'severity',
    displayName: (
      <Space
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="center"
      >
        <Text>Severity</Text>
        <Popup
          content={popupText('severity')}
          trigger={
            <StyledSpan>
              <CustomIcons componentName="INFO" />
            </StyledSpan>
          }
        />
      </Space>
    ),
    toolTip: true,
  },
  {
    key: 'status',
    displayName: 'Status',
    toolTip: true,
  },
];

export const SEARCH_INCIDENTS_URL = {
  url: '/pg/incidents/search',
  method: 'GET',
};

export const GET_INCIDENTS_SUMMARY_URL = {
  url: '/pg/incidents/summary',
  method: 'GET',
};

export const CARD_FILTERS = {
  RESOLVED: {
    dateFilters: {
      displayText: 'All time',
      range: [
        moment()
          .subtract(365, 'day')
          .toDate(),
        moment().toDate(),
      ],
    },
    appliedFilters: {
      startDate: moment()
        .subtract(365, 'day')
        .format('YYYY-MM-DD 00:00:00'),
      endDate: moment().format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT),
    },
    filtersForDisplay: [
      { displayText: 'Resolved', key: 'status', value: RESOLVED },
    ],
  },
  ACTIVE: {
    dateFilters: {
      displayText: 'All time',
      range: [
        moment()
          .subtract(365, 'day')
          .toDate(),
        moment().toDate(),
      ],
    },
    appliedFilters: {
      startDate: moment()
        .subtract(365, 'day')
        .format('YYYY-MM-DD 00:00:00'),
      endDate: moment().format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT),
    },
    filtersForDisplay: [
      { displayText: 'Active', key: 'status', value: ACTIVE },
    ],
  },
  UPCOMING: {
    dateFilters: {
      displayText: 'Next 48 hours',
      range: [
        moment().toDate(),
        moment()
          .add(48, 'hours')
          .toDate(),
      ],
    },
    appliedFilters: {
      startDate: moment().format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT),
      endDate: moment()
        .add(48, 'hours')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT),
    },
    filtersForDisplay: [
      {
        displayText: 'Upcoming',
        key: 'status',
        value: UPCOMING,
      },
    ],
  },
  DEFAULT: {
    dateFilters: {
      displayText: 'All time',
      range: [
        moment()
          .subtract(365, 'day')
          .toDate(),
        moment()
          .add(180, 'day')
          .toDate(),
      ],
    },
    appliedFilters: {
      startDate: moment()
        .subtract(365, 'day')
        .format('YYYY-MM-DD 00:00:00'),
      endDate: moment()
        .add(180, 'day')
        .format('YYYY-MM-DD 23:59:59'),
    },
    filtersForDisplay: [],
  },
};

export const RANGE_OBJECT = {
  UPCOMING: ['All time', 'Today', 'This Month', 'Next 7 days'],
  DEFAULT: ['Last 24 hours', 'Last 48 hours', 'Next 24 hours', 'Next 48 hours'],
};

export const ALL_BANKS = 'ALL BANKS';

export const ALL_PSP = 'All PSP';

export const UPI = 'UPI';
